/**
 * Firewood pile (`hush.firewood_pile.v1`) — a ground piece for the hideout yard.
 *
 * Frame 120×70, support at the bottom-center (60, 70). A neat stack of split-log ends (`rows` 2..4, brick-offset)
 * sits on the left, a chopping block stands on the right with an optional axe bitten into its top (`axe`), and a
 * few wood chips lie on a strip of packed earth. End-grain rings are drawn sparsely: at most one ringed end per row
 * and never in two adjacent rows, so no pair of ring ends can sit side by side above a third (reads as a face).
 * No definitions, no collision or gameplay geometry. Deterministic for the recipe; the seed varies the logs.
 */
import {
  checkEnvelope, checkScope, el, mix, n, partGroup, poly, rngFor,
  type HideoutPieceArt, type PieceRecipe, type Point, type ResolvedPalette,
} from '../pieces/svg';

export interface FirewoodParams {
  readonly rows: number; // integer 2..4
  readonly axe: boolean;
}
export type FirewoodRecipe = PieceRecipe<'hush.firewood_pile.v1', FirewoodParams>;

export const FIREWOOD_FRAME = { w: 120, h: 70 } as const;

export const HUSH_FIREWOOD: FirewoodRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'hush.firewood_pile.yard.v1', generatorId: 'hush.firewood_pile.v1',
  seed: 20067018, paletteId: 'coastal_salt', params: { rows: 3, axe: true },
};

/** rngFor's first draw is correlated across similar channel names; a murmur finalizer decorrelates it. */
function scrambled(base: (ch: string) => number): (ch: string) => number {
  return ch => {
    let h = Math.floor(base(ch) * 4294967296) >>> 0;
    h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b); h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35); h ^= h >>> 16;
    return (h >>> 0) / 4294967296;
  };
}

const W = FIREWOOD_FRAME.w, H = FIREWOOD_FRAME.h;
const clamp = (p: Point): Point => ({ x: Math.min(W - 1.5, Math.max(1.5, p.x)), y: Math.min(H - 1, Math.max(1.5, p.y)) });

function params(p: unknown): { rows: number; axe: boolean } {
  const r = p as Record<string, unknown>;
  if (typeof r.rows !== 'number' || !Number.isInteger(r.rows) || r.rows < 2 || r.rows > 4) {
    throw new Error('hush.firewood_pile.v1: rows must be an integer 2..4');
  }
  if (typeof r.axe !== 'boolean') throw new Error('hush.firewood_pile.v1: axe must be boolean');
  return { rows: r.rows, axe: r.axe };
}

/** A split-log end: a round bark side and a flatter split side, rotated by `turn`. */
function logEnd(cx: number, cy: number, r: number, turn: number, jag: (i: number) => number): Point[] {
  const pts: Point[] = [];
  const K = 14;
  for (let k = 0; k < K; k++) {
    const a = turn + (Math.PI * 2 * k) / K;
    // the split face flattens one side of the round
    const c = Math.cos(a - turn);
    const flat = c > 0.78 ? 0.78 / c : 1;
    const rr = r * flat * (0.94 + 0.06 * jag(k));
    pts.push(clamp({ x: cx + Math.cos(a) * rr * 1.08, y: cy + Math.sin(a) * rr * 0.92 }));
  }
  return pts;
}

export function buildFirewood(recipe: FirewoodRecipe, roles: ResolvedPalette, scope: string): HideoutPieceArt {
  checkEnvelope(recipe, 'hush.firewood_pile.v1');
  checkScope(scope);
  const { rows, axe } = params(recipe.params);
  const rnd = scrambled(rngFor(recipe));
  const parts: string[] = [];
  const outline = roles.outline;

  // ---- ground: a strip of packed earth the pile and block sit on ----
  const groundTop = H - 5;
  const ground = el('path', {
    d: `M2 ${H}Q4 ${n(groundTop)} 14 ${n(groundTop)}L106 ${n(groundTop + 0.5)}Q116 ${n(groundTop + 1)} 118 ${H}Z`,
    fill: mix(roles.stoneShadow, roles.woodShadow, 0.4),
  }) + el('path', { d: `M10 ${n(groundTop + 1.2)}L108 ${n(groundTop + 1.6)}`, fill: 'none', stroke: roles.stoneBase, 'stroke-width': 0.8, opacity: 0.5 });

  // ---- stack: brick-offset rows of split-log ends ----
  const r = 6.4;
  const perRow = 5;
  const left = 6, rowH = r * 1.75;
  const baseY = groundTop - r * 0.85;
  // Ring ends: at most one per row, never in adjacent rows (anti-face).
  const ringRows = new Set<number>();
  for (let row = 0; row < rows; row++) if (!ringRows.has(row - 1) && rnd(`ring.row.${row}`) < 0.7) ringRows.add(row);
  let stack = '', rings = '';
  for (let row = 0; row < rows; row++) {
    const count = perRow - (row === rows - 1 && rows > 2 ? 1 : 0);
    const off = (row % 2) * r * 1.05 + (perRow - count) * r * 1.05 * (row % 2 ? 0 : 1);
    const cy = baseY - row * rowH;
    const ringAt = ringRows.has(row) ? Math.floor(rnd(`ring.at.${row}`) * count) : -1;
    for (let i = 0; i < count; i++) {
      const key = `log.${row}.${i}`;
      const cx = left + r * 1.1 + off + i * r * 2.15 + (rnd(`${key}.dx`) - 0.5) * 1.2;
      const y = cy + (rnd(`${key}.dy`) - 0.5) * 1;
      const rr = r * (0.9 + rnd(`${key}.r`) * 0.15);
      const turn = -Math.PI / 2 + (rnd(`${key}.turn`) - 0.5) * 2.4;
      const pts = logEnd(cx, y, rr, turn, k => rnd(`${key}.j${k}`) * 2 - 1);
      // bark ring then the pale end grain inset
      stack += el('path', { d: poly(pts), fill: roles.woodShadow, stroke: outline, 'stroke-width': 1.1, 'stroke-linejoin': 'round' });
      const inner = pts.map(p => ({ x: cx + (p.x - cx) * 0.78, y: y + (p.y - y) * 0.78 }));
      const tone = rnd(`${key}.tone`);
      stack += el('path', { d: poly(inner), fill: tone < 0.5 ? roles.woodLight : mix(roles.woodLight, roles.paperBase, 0.35) });
      if (i === ringAt) {
        rings += el('ellipse', { cx, cy: y, rx: rr * 0.42, ry: rr * 0.36, fill: 'none', stroke: roles.woodBase, 'stroke-width': 0.7, opacity: 0.8 });
        rings += el('ellipse', { cx, cy: y, rx: rr * 0.18, ry: rr * 0.15, fill: 'none', stroke: roles.woodBase, 'stroke-width': 0.6, opacity: 0.7 });
      } else {
        // a single split check line keeps plain ends from reading as blank discs
        const a = turn + Math.PI + (rnd(`${key}.check`) - 0.5) * 0.8;
        stack += el('path', {
          d: `M${n(cx)} ${n(y)}L${n(cx + Math.cos(a) * rr * 0.6)} ${n(y + Math.sin(a) * rr * 0.55)}`,
          fill: 'none', stroke: roles.woodBase, 'stroke-width': 0.6, opacity: 0.7,
        });
      }
    }
  }
  const stackTop = baseY - (rows - 1) * rowH - r;

  // ---- chopping block: a squat round with a pale top ----
  const bx = 96, bw = 13, bTop = groundTop - 15, bBot = groundTop + 1.5;
  let block = el('path', {
    d: `M${n(bx - bw)} ${n(bTop)}L${n(bx - bw - 0.8)} ${n(bBot)}Q${n(bx)} ${n(bBot + 3)} ${n(bx + bw + 0.8)} ${n(bBot)}L${n(bx + bw)} ${n(bTop)}Z`,
    fill: roles.woodBase, stroke: outline, 'stroke-width': 1.2, 'stroke-linejoin': 'round',
  });
  for (let i = 0; i < 3; i++) {
    const x = bx - bw + 4 + i * 8 + (rnd(`block.bark.${i}`) - 0.5) * 2;
    block += el('path', { d: `M${n(x)} ${n(bTop + 4)}L${n(x + 0.6)} ${n(bBot - 2)}`, fill: 'none', stroke: roles.woodShadow, 'stroke-width': 1, opacity: 0.7 });
  }
  block += el('ellipse', { cx: bx, cy: bTop, rx: bw, ry: 3.4, fill: roles.woodLight, stroke: outline, 'stroke-width': 1.1 });
  block += el('ellipse', { cx: bx - 2, cy: bTop - 0.3, rx: bw * 0.45, ry: 1.4, fill: 'none', stroke: roles.woodBase, 'stroke-width': 0.6, opacity: 0.7 });

  // ---- axe: head bitten into the block top, handle leaning up-left ----
  let axeArt = '';
  if (axe) {
    const hx = bx + 3 + (rnd('axe.x') - 0.5) * 3, hy = bTop + 0.5;
    const tip = clamp({ x: hx - 15, y: bTop - 21 - rnd('axe.lift') * 3 });
    axeArt += el('path', { d: `M${n(hx)} ${n(hy - 1)}L${n(tip.x)} ${n(tip.y)}`, fill: 'none', stroke: outline, 'stroke-width': 3.4, 'stroke-linecap': 'round' });
    axeArt += el('path', { d: `M${n(hx)} ${n(hy - 1)}L${n(tip.x)} ${n(tip.y)}`, fill: 'none', stroke: roles.woodLight, 'stroke-width': 1.8, 'stroke-linecap': 'round' });
    axeArt += el('path', {
      d: poly([{ x: hx - 3, y: hy - 4.5 }, { x: hx + 6.5, y: hy - 6 }, { x: hx + 7.5, y: hy + 0.6 }, { x: hx - 1, y: hy + 0.8 }]),
      fill: roles.stoneBase, stroke: outline, 'stroke-width': 1, 'stroke-linejoin': 'round',
    });
    axeArt += el('path', { d: `M${n(hx + 5.8)} ${n(hy - 5.2)}L${n(hx + 6.6)} ${n(hy - 0.2)}`, fill: 'none', stroke: roles.stoneLight, 'stroke-width': 0.9 });
  }

  // ---- chips: a few pale splinters scattered round the block ----
  let chips = '';
  for (let i = 0; i < 6; i++) {
    const x = 70 + rnd(`chip.${i}.x`) * 44, y = groundTop + 0.8 + rnd(`chip.${i}.y`) * 2.5;
    const a = rnd(`chip.${i}.a`) * Math.PI, l = 2 + rnd(`chip.${i}.l`) * 2;
    const p0 = clamp({ x: x - Math.cos(a) * l, y: y - Math.sin(a) * 0.8 }), p1 = clamp({ x: x + Math.cos(a) * l, y: y + Math.sin(a) * 0.8 });
    chips += el('path', { d: poly([p0, clamp({ x, y: y - 1.1 }), p1, clamp({ x, y: y + 0.6 })]), fill: roles.woodLight, stroke: roles.woodShadow, 'stroke-width': 0.5 });
  }

  const body = partGroup(parts, 'firewood-ground', ground)
    + partGroup(parts, 'firewood-stack', stack)
    + partGroup(parts, 'firewood-rings', rings)
    + partGroup(parts, 'firewood-block', block)
    + (axe ? partGroup(parts, 'firewood-axe', axeArt) : '')
    + partGroup(parts, 'firewood-chips', chips);
  const support = { x: W / 2, y: H };
  return {
    frame: { ...FIREWOOD_FRAME },
    body,
    anchors: { support, stack_top: { x: left + perRow * r * 1.1, y: stackTop }, block_top: { x: bx, y: bTop } },
    parts,
  };
}
